import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './DiagramList.css';

const getToken = () => localStorage.getItem('notes_token');

const apiFetch = async (url, opts = {}) => {
  const token = getToken();
  if (!opts.headers) opts.headers = {};
  if (token) opts.headers['x-token'] = token;
  const res = await fetch(url, opts);
  if (res.status === 401) {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    window.location.href = '/login';
    return res;
  }
  return res;
};

function DiagramList() {
  const navigate = useNavigate();
  const [diagrams, setDiagrams] = useState([]);
  const [sharedDiagrams, setSharedDiagrams] = useState([]);
  const [folders, setFolders] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareUserId, setShareUserId] = useState('');
  const [activeTab, setActiveTab] = useState('my');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [dragItem, setDragItem] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    console.log('loadData called, activeTab:', activeTab);
    setLoading(true);
    try {
      if (activeTab === 'my') {
        const [diagRes, folderRes, userRes] = await Promise.all([
          apiFetch('/api/diagrams'),
          apiFetch('/api/diagrams/folders'),
          apiFetch('/api/diagrams/users')
        ]);
        if (diagRes.ok) {
          const data = await diagRes.json();
          console.log('Diagrams loaded:', data.data?.length || 0);
          setDiagrams(data.data || []);
        }
        if (folderRes.ok) {
          const data = await folderRes.json();
          console.log('Folders loaded:', data.data?.length || 0);
          setFolders(data.data || []);
        }
        if (userRes.ok) {
          const data = await userRes.json();
          setUsers(data.data || []);
        }
      } else {
        const sharedRes = await apiFetch('/api/diagrams/shared');
        if (sharedRes.ok) {
          const data = await sharedRes.json();
          setSharedDiagrams(data.data || []);
        }
      }
    } catch (e) {
      console.error('加载失败:', e);
    }
    setLoading(false);
  };

  // 构建树形结构
  const buildTree = useCallback(() => {
    console.log('buildTree called, folders:', folders.length, 'diagrams:', diagrams.length);
    const folderMap = {};
    folders.forEach(f => { folderMap[f.id] = { ...f, children: [], diagrams: [] }; });
    
    // 初始化根目录
    folderMap['root'] = { id: 'root', name: '根目录', children: [], diagrams: [] };
    
    // 添加子文件夹
    folders.forEach(f => {
      if (f.parentId && folderMap[f.parentId]) {
        // 有父文件夹，添加到父文件夹的 children
        folderMap[f.parentId].children.push(folderMap[f.id]);
      } else {
        // 没有父文件夹（顶层文件夹），添加到根目录的 children
        folderMap['root'].children.push(folderMap[f.id]);
      }
    });
    
    // 添加流程图
    diagrams.forEach(d => {
      const targetId = d.folderId || 'root';
      if (targetId === 'root') {
        folderMap['root'].diagrams.push(d);
      } else if (folderMap[targetId]) {
        folderMap[targetId].diagrams.push(d);
      }
    });
    
    console.log('buildTree result, rootFolder diagrams:', folderMap['root']?.diagrams?.length || 0, 'children:', folderMap['root']?.children?.length || 0);
    return folderMap;
  }, [folders, diagrams]);

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // 拖拽处理
  const handleDragStart = (e, diagram) => {
    setDragItem(diagram);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, targetFolderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    if (!dragItem || dragItem.folderId === targetFolderId) {
      setDragItem(null);
      return;
    }
    
    const res = await apiFetch(`/api/diagrams/${dragItem.id}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderId: targetFolderId })
    });
    
    if (res.ok) {
      loadData();
    }
    setDragItem(null);
  };

  const handleCreate = async () => {
    const name = prompt('请输入流程图名称:');
    if (!name) return;
    
    const res = await apiFetch('/api/diagrams', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, folderId: currentFolder })
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        navigate(`/diagrams/editor/${data.data.id}`);
      }
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`确定删除「${name}」？`)) return;
    
    const res = await apiFetch(`/api/diagrams/${id}`, {
      method: 'DELETE'
    });
    if (res.ok) {
      loadData();
    }
  };

  const handleRename = async (id, oldName) => {
    const newName = prompt('请输入新名称:', oldName);
    if (!newName || newName === oldName) return;
    
    const res = await apiFetch(`/api/diagrams/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName })
    });
    if (res.ok) {
      loadData();
    }
  };

  const handleCreateFolder = async () => {
    console.log('handleCreateFolder called, newFolderName:', newFolderName);
    if (!newFolderName.trim()) {
      alert('请输入文件夹名称');
      return;
    }
    
    try {
      console.log('Creating folder:', newFolderName.trim(), 'parentId:', newFolderParent);
      const res = await apiFetch('/api/diagrams/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFolderName.trim(), parentId: newFolderParent })
      });
      
      console.log('Create folder response:', res.status);
      if (res.ok) {
        const data = await res.json();
        console.log('Create folder data:', data);
        if (data.success) {
          setShowNewFolder(false);
          setNewFolderName('');
          setNewFolderParent(null);
          loadData();
        } else {
          alert('创建失败: ' + (data.error || '未知错误'));
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        alert('创建失败: ' + (errData.error || '请求失败'));
      }
    } catch (e) {
      console.error('创建文件夹失败:', e);
      alert('创建失败: ' + e.message);
    }
  };

  const handleDeleteFolder = async (id, name) => {
    if (!confirm(`确定删除文件夹「${name}」及其内容？`)) return;
    
    const res = await apiFetch(`/api/diagrams/folders/${id}`, {
      method: 'DELETE'
    });
    if (res.ok) {
      if (currentFolder === id) setCurrentFolder(null);
      loadData();
    }
  };

  const handleShare = (diagram) => {
    setShareTarget(diagram);
    setShowShareModal(true);
  };

  const doShare = async () => {
    if (!shareUserId) return;
    
    const res = await apiFetch(`/api/diagrams/${shareTarget.id}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: shareUserId })
    });
    if (res.ok) {
      setShowShareModal(false);
      setShareUserId('');
      loadData();
    }
  };

  const handleUnshare = async (diagramId, userId) => {
    const res = await apiFetch(`/api/diagrams/${diagramId}/share/${userId}`, {
      method: 'DELETE'
    });
    if (res.ok) {
      loadData();
    }
  };

  const handleCopy = async (id) => {
    const res = await apiFetch(`/api/diagrams/${id}/copy`, {
      method: 'POST'
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        alert('已复制到你的流程图');
        setActiveTab('my');
      }
    }
  };

  // 渲染文件夹树节点
  const renderFolderNode = (folder, level = 0) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;
    const hasDiagrams = folder.diagrams && folder.diagrams.length > 0;
    const isDragOver = dragOverFolder === folder.id;
    
    const handleClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      console.log('Folder clicked:', folder.id, folder.name);
      setCurrentFolder(folder.id);
      toggleFolder(folder.id);
    };
    
    return (
      <div key={folder.id} className="folder-tree-node" style={{ paddingLeft: level * 16 }}>
        <div 
          className={`folder-tree-item ${currentFolder === folder.id ? 'active' : ''} ${isDragOver ? 'drag-over' : ''}`}
          onClick={handleClick}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, folder.id)}
        >
          <span className="folder-expand-icon">
            {hasChildren || hasDiagrams ? (isExpanded ? '▼' : '▶') : '•'}
          </span>
          <span className="folder-tree-icon">📁</span>
          <span className="folder-tree-name">{folder.name}</span>
          <span className="folder-tree-count">{(folder.diagrams || []).length}</span>
          <button 
            className="folder-tree-delete"
            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id, folder.name); }}
            title="删除文件夹"
          >
            ✕
          </button>
        </div>
        {isExpanded && folder.children && folder.children.map(child => renderFolderNode(child, level + 1))}
      </div>
    );
  };

  const treeData = buildTree();
  const rootFolder = treeData['root'] || { id: 'root', name: '根目录', children: [], diagrams: [] };
  const currentDiagrams = activeTab === 'my' 
    ? (currentFolder ? (treeData[currentFolder]?.diagrams || []) : rootFolder.diagrams)
    : sharedDiagrams;
  const currentFolderInfo = folders.find(f => f.id === currentFolder);

  return (
    <div className="diagram-list-page">
      <div className="diagram-header">
        <h2>📊 流程图</h2>
        <div className="diagram-actions">
          {activeTab === 'my' && (
            <>
              <button onClick={() => { setNewFolderParent(currentFolder); setShowNewFolder(true); }} className="btn-secondary">
                📁 新建文件夹
              </button>
              <button onClick={handleCreate} className="btn-primary">
                ➕ 新建流程图
              </button>
            </>
          )}
        </div>
      </div>

      <div className="diagram-tabs">
        <button 
          className={activeTab === 'my' ? 'active' : ''} 
          onClick={() => setActiveTab('my')}
        >
          我的流程图
        </button>
        <button 
          className={activeTab === 'shared' ? 'active' : ''} 
          onClick={() => setActiveTab('shared')}
        >
          共享给我的
        </button>
      </div>

      {loading ? (
        <div className="diagram-loading">加载中...</div>
      ) : (
        <div className="diagram-layout">
          {/* 左侧文件夹树 */}
          {activeTab === 'my' && (
            <div className="diagram-sidebar">
              <div className="sidebar-header">
                <span>文件夹</span>
              </div>
              <div className="folder-tree">
                {/* 根目录 */}
                <div 
                  className={`folder-tree-item root ${currentFolder === null ? 'active' : ''} ${dragOverFolder === null ? 'drag-over' : ''}`}
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    console.log('Root folder clicked, currentFolder:', currentFolder);
                    setCurrentFolder(null); 
                  }}
                  onDragOver={(e) => handleDragOver(e, null)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, null)}
                >
                  <span className="folder-expand-icon">▶</span>
                  <span className="folder-tree-icon">🏠</span>
                  <span className="folder-tree-name">根目录</span>
                  <span className="folder-tree-count">{rootFolder.diagrams.length}</span>
                </div>
                {/* 子文件夹 */}
                {rootFolder.children && rootFolder.children.map(folder => renderFolderNode(folder, 1))}
              </div>
            </div>
          )}

          {/* 右侧流程图列表 */}
          <div className="diagram-main">
            {activeTab === 'my' && currentFolderInfo && (
              <div className="diagram-breadcrumb">
                <span onClick={() => setCurrentFolder(null)} className="clickable">根目录</span>
                <span> / </span>
                <span>{currentFolderInfo.name}</span>
              </div>
            )}

            {currentDiagrams.length > 0 ? (
              <div className="diagram-list">
                {currentDiagrams.map(diagram => (
                  <div 
                    key={diagram.id} 
                    className="diagram-list-item"
                    draggable={activeTab === 'my'}
                    onDragStart={(e) => handleDragStart(e, diagram)}
                  >
                    <div className="diagram-list-icon" onClick={() => navigate(`/diagrams/editor/${diagram.id}`)}>
                      📄
                    </div>
                    <div className="diagram-list-info">
                      <div className="diagram-list-name" onClick={() => navigate(`/diagrams/editor/${diagram.id}`)}>
                        {diagram.name}
                      </div>
                      <div className="diagram-list-meta">
                        {activeTab === 'my' ? (
                          <>
                            <span>更新于 {new Date(diagram.updatedAt).toLocaleDateString()}</span>
                            {diagram.shares && diagram.shares.length > 0 && (
                              <span className="share-count">已共享 {diagram.shares.length} 人</span>
                            )}
                          </>
                        ) : (
                          <>来自 {diagram.ownerName || diagram.owner}</>
                        )}
                      </div>
                    </div>
                    <div className="diagram-list-actions">
                      {activeTab === 'my' ? (
                        <>
                          <button onClick={() => handleRename(diagram.id, diagram.name)} className="btn-action btn-rename">重命名</button>
                          <button onClick={() => handleCopy(diagram.id)} className="btn-action btn-copy">复制</button>
                          <button onClick={() => handleShare(diagram)} className="btn-action btn-share">共享</button>
                          <button onClick={() => handleDelete(diagram.id, diagram.name)} className="btn-action btn-delete">删除</button>
                        </>
                      ) : (
                        <button onClick={() => handleCopy(diagram.id)} className="btn-action btn-copy">复制</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="diagram-empty">
                {activeTab === 'my' ? '暂无流程图，点击上方按钮创建' : '暂无共享给你的流程图'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 新建文件夹弹窗 */}
      {showNewFolder && (
        <div className="modal-overlay" onClick={() => setShowNewFolder(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>新建文件夹</h3>
            <input 
              type="text" 
              value={newFolderName} 
              onChange={e => setNewFolderName(e.target.value)}
              placeholder="文件夹名称"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder();
                }
              }}
            />
            <div className="modal-actions">
              <button onClick={() => setShowNewFolder(false)}>取消</button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateFolder();
                }}
                className="btn-primary"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 共享弹窗 */}
      {showShareModal && shareTarget && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>共享流程图</h3>
            
            {shareTarget.shares && shareTarget.shares.length > 0 && (
              <div className="share-list">
                <label>已共享给:</label>
                {shareTarget.shares.map(share => (
                  <div key={share.userId} className="share-item">
                    <span>{share.userId}</span>
                    <button onClick={() => handleUnshare(shareTarget.id, share.userId)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="share-add">
              <label>添加用户:</label>
              <select value={shareUserId} onChange={e => setShareUserId(e.target.value)}>
                <option value="">选择用户</option>
                {users.filter(u => u.username !== shareTarget.owner).map(u => (
                  <option key={u.username} value={u.username}>
                    {u.displayName} ({u.username})
                  </option>
                ))}
              </select>
              <button onClick={doShare} className="btn-primary" disabled={!shareUserId}>确定</button>
            </div>
            
            <div className="modal-actions">
              <button onClick={() => setShowShareModal(false)}>关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DiagramList;
